import type { CustomerDetail } from './adminApi';

/**
 * Companies House annual-accounts deadline calculator for UK private
 * limited companies filing dormant accounts.
 *
 * The rules (per CA 2006 s.442 + s.443):
 *   - ARD (Accounting Reference Date) = the accounts' period end.
 *   - Filing deadline = 9 months after the ARD for private companies.
 *   - The next ARD rolls 12 months from the previous one (assuming the
 *     company hasn't shortened/extended the period at CH).
 *
 * For each of a customer's companies, we find the most recent
 * accepted-or-filed periodEnd and project the next deadline from there.
 */

export interface CompanyDeadline {
  companyName: string;
  companyNumber: string;
  /** ISO date — the next period the customer will need to file for. */
  nextPeriodEnd: string;
  /** ISO date — the date Companies House expect the filing by. */
  filingDeadline: string;
  /** Days from today to the filing deadline. Negative = overdue. */
  daysUntilDeadline: number;
  urgency: 'overdue' | 'urgent' | 'soon' | 'later';
}

/**
 * Compute upcoming CH deadlines per company for a customer. Returns
 * earliest deadline first. Companies with no successful filing yet
 * are omitted — we can't project a deadline from a rejected order
 * because no ARD was recorded with CH.
 */
export function computeCustomerDeadlines(customer: CustomerDetail): CompanyDeadline[] {
  const byCompany = new Map<string, CompanyDeadline>();

  for (const order of customer.orders) {
    // Only project from filings CH actually accepted — a rejected order
    // has not changed the company's ARD at CH so we can't roll forward
    // from it.
    if (order.status !== 'ACCEPTED') continue;
    if (!order.periodEnd) continue;

    const next = nextDeadline(order.periodEnd);
    const existing = byCompany.get(order.companyNumber);
    // Keep the latest filing per company.
    if (
      !existing ||
      new Date(order.periodEnd) > new Date(rollBackOneYear(existing.nextPeriodEnd))
    ) {
      byCompany.set(order.companyNumber, {
        companyName: order.companyName,
        companyNumber: order.companyNumber,
        ...next,
      });
    }
  }

  return [...byCompany.values()].sort(
    (a, b) => a.daysUntilDeadline - b.daysUntilDeadline,
  );
}

function nextDeadline(periodEndIso: string): Omit<CompanyDeadline, 'companyName' | 'companyNumber'> {
  // Roll the period forward 12 months.
  const lastPE = new Date(periodEndIso + 'T00:00:00Z');
  const nextPE = new Date(lastPE);
  nextPE.setUTCFullYear(nextPE.getUTCFullYear() + 1);

  // Filing deadline = 9 months after the new ARD.
  const deadline = new Date(nextPE);
  deadline.setUTCMonth(deadline.getUTCMonth() + 9);

  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const days = Math.round((deadline.getTime() - today.getTime()) / 86_400_000);

  let urgency: CompanyDeadline['urgency'];
  if (days < 0) urgency = 'overdue';
  else if (days <= 30) urgency = 'urgent';
  else if (days <= 90) urgency = 'soon';
  else urgency = 'later';

  return {
    nextPeriodEnd: nextPE.toISOString().slice(0, 10),
    filingDeadline: deadline.toISOString().slice(0, 10),
    daysUntilDeadline: days,
    urgency,
  };
}

function rollBackOneYear(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCFullYear(d.getUTCFullYear() - 1);
  return d.toISOString().slice(0, 10);
}

export function urgencyTone(urgency: CompanyDeadline['urgency']): string {
  switch (urgency) {
    case 'overdue':
      return 'bg-red-50 text-red-700 ring-red-200';
    case 'urgent':
      return 'bg-amber-50 text-amber-700 ring-amber-200';
    case 'soon':
      return 'bg-blue-50 text-blue-700 ring-blue-200';
    case 'later':
    default:
      return 'bg-slate-50 text-slate-600 ring-slate-200';
  }
}

export function formatDeadlineDate(iso: string): string {
  return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatDeadlineRelative(days: number): string {
  if (days < -30) return `${-days} days overdue`;
  if (days < 0) return `${-days} day${days === -1 ? '' : 's'} overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days <= 60) return `In ${days} days`;
  const months = Math.round(days / 30);
  return `In ~${months} months`;
}
